import React from 'react';
import { Box, styled, keyframes } from '@mui/material';

// Generated by Copilot

const shimmerAnimation = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

interface ShimmerEffectProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular' | 'rounded';
  lines?: number; // For text variant - number of lines to show
  spacing?: number; // For text variant - spacing between lines
  children?: React.ReactNode; // For custom content
}

interface ShimmerContainerProps {
  shimmerWidth?: string | number;
  shimmerHeight?: string | number;
  shimmerBorderRadius?: string | number;
  className?: string;
  children?: React.ReactNode;
  sx?: any;
}

const ShimmerContainer = styled(
  ({ shimmerWidth, shimmerHeight, shimmerBorderRadius, className, children, sx, ...rest }: ShimmerContainerProps) => (
    <Box className={className} sx={sx} {...rest}>
      {children}
    </Box>
  )
)<ShimmerContainerProps>(({ shimmerWidth, shimmerHeight, shimmerBorderRadius }) => ({
  width: shimmerWidth || '100%',
  height: shimmerHeight || '20px',
  borderRadius: shimmerBorderRadius || '4px',
  background: `linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 37%,
    #f0f0f0 63%
  )`,
  backgroundSize: '400% 100%',
  animation: `${shimmerAnimation} 1.5s ease-in-out infinite`,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

interface ShimmerLineProps {
  lineHeight?: string | number;
  lineWidth?: string | number;
  isLastLine?: boolean;
  className?: string;
  sx?: any;
}

const ShimmerLine = styled(
  ({ lineHeight, lineWidth, isLastLine, className, sx, ...rest }: ShimmerLineProps) => (
    <Box className={className} sx={sx} {...rest} />
  )
)<ShimmerLineProps>(({ lineHeight, lineWidth, isLastLine }) => ({
  width: lineWidth || (isLastLine ? '60%' : '100%'),
  height: lineHeight || '16px',
  borderRadius: '4px',
  background: `linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 37%,
    #f0f0f0 63%
  )`,
  backgroundSize: '400% 100%',
  animation: `${shimmerAnimation} 1.5s ease-in-out infinite`,
}));

const ShimmerEffect: React.FC<ShimmerEffectProps> = ({
  width = '100%',
  height = '20px',
  borderRadius = '4px',
  className = '',
  variant = 'rectangular',
  lines = 3,
  spacing = 8,
  children,
}) => {
  // Handle different variants
  const getVariantStyles = () => {
    switch (variant) {
      case 'circular':
        return {
          width: width || height || '40px',
          height: height || width || '40px',
          borderRadius: '50%',
        };
      case 'rounded':
        return {
          width,
          height,
          borderRadius: '8px',
        };
      case 'text':
        return {
          width,
          height: 'auto',
          borderRadius: 0,
          background: 'transparent',
        };
      default:
        return {
          width,
          height,
          borderRadius,
        };
    }
  };

  const variantStyles = getVariantStyles();

  // If it's a text variant, render multiple lines
  if (variant === 'text') {
    return (
      <Box className={`shimmer-effect ${className}`} sx={{ width: variantStyles.width }}>
        {Array.from({ length: lines }).map((_, index) => (
          <ShimmerLine
            key={index}
            lineHeight={height}
            isLastLine={index === lines - 1}
            sx={{ 
              marginBottom: index < lines - 1 ? `${spacing}px` : 0,
              animationDelay: `${index * 0.1}s`,
            }}
          />
        ))}
      </Box>
    );
  }

  // For custom content (children), render a container with shimmer background
  if (children) {
    return (
      <ShimmerContainer
        className={`shimmer-effect ${className}`}
        shimmerWidth={variantStyles.width}
        shimmerHeight={variantStyles.height}
        shimmerBorderRadius={variantStyles.borderRadius}
        sx={{
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.3,
          }}
        >
          {children}
        </Box>
      </ShimmerContainer>
    );
  }

  // Default rectangular or circular shimmer
  return (
    <ShimmerContainer
      className={`shimmer-effect ${className}`}
      shimmerWidth={variantStyles.width}
      shimmerHeight={variantStyles.height}
      shimmerBorderRadius={variantStyles.borderRadius}
    />
  );
};

export default ShimmerEffect;
